import { FC, Fragment, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import useRecentRepayments from "../../../../../custom-hooks/useRecentRepayments";
import useDashboardRepaymentGraph from "../../../../../custom-hooks/useRepaymentGraph";
import { formatGraphData } from "../../../../../helpers/formatGraphData";
import { formatMoney } from "accounting";
import graphDropdownOptions from "../../../../../helpers/graphDropdownOptions";
import { GRAPH_SCALE } from "../../../../../helpers/graphOptions";
import GraphSubTitle from "../GraphComponents/GraphSubTitleSection/GraphSubTitle.component";
import GraphTitleSection from "../GraphComponents/GraphTitleSection/GraphTitleSection.component";
import BarChartSingle from "../Charts/BarChartSingle/BarChartSingle.component";
import EmptyBarChartSingle from "../Charts/BarChartSingle/EmptyBarChartSingle.component";
import ButtonLoader from "../../../../../UI/Loaders/Loaders";

interface IRepaymentsGraph {
  aggregatorId: number;
  currentCurrency: string;
}

const RepaymentsGraph: FC<IRepaymentsGraph> = ({
  aggregatorId,
  currentCurrency,
}) => {
  const [filter, setFilter] = useState(graphDropdownOptions.thisYear);
  const match = useRouteMatch();
  const { data, status, error } = useDashboardRepaymentGraph({
    aggregatorId,
    filter,
    currentCurrencyValue: currentCurrency,
  });

  const {
    data: recentRepaymentsData,
    status: recentRepaymentsStatus,
    error: recentRepaymentsError,
  } = useRecentRepayments({
    aggregatorId,
    currentCurrencyValue: currentCurrency,
  });

  //JSX Template
  let RenderedComponent = null;

  if (status === "loading") {
    RenderedComponent = (
      <div className="d-flex justify-content-center align-items-center m-h-100">
        <ButtonLoader />
      </div>
    );
  } else if (error) {
    RenderedComponent = (
      <h5 className="text-danger text-center font-weight-bold">
        An error occurred
      </h5>
    );
  } else if (data) {
    if (data?.data?.length < 0) {
      // Please remember to alter this when valid data is available
      RenderedComponent = <EmptyBarChartSingle />;
    } else {
      RenderedComponent = (
        <BarChartSingle
          isDisbursement={false}
          graphData={formatGraphData(data.data)}
        />
      );
    }
  }

  return (
    <div>
      <div className="card">
        <div className="card-body p-3">
          <GraphTitleSection
            filter={filter}
            setFilter={setFilter}
            title="Repayments"
          >
            <p className="text-dark">
              in {GRAPH_SCALE.toLocaleString("en-NG")}
            </p>
          </GraphTitleSection>
          <Fragment>{RenderedComponent}</Fragment>
        </div>
      </div>
      <div className="card mt-3">
        <div className="card-body p-3">
          <GraphSubTitle
            text="Recent Repayments"
            to={match.path + "repayments"}
          />

          <Fragment>
            {recentRepaymentsStatus === "loading" ? (
              <div className="d-flex justify-content-center align-items-center m-h-100">
                <ButtonLoader />
              </div>
            ) : recentRepaymentsError ? (
              <h5 className="text-danger text-center font-weight-bold">
                An error occurred
              </h5>
            ) : recentRepaymentsData && recentRepaymentsData.length > 0 ? (
              <ul className="dashboard-graph-list">
                {recentRepaymentsData
                  .slice(0, 3)
                  .map((data: any, _i: number) => {
                    const { borrower_full_name, repayment_amount, currency } =
                      data;
                    return (
                      <li key={_i}>
                        <span className="dashbord-graph-list-name">
                          {borrower_full_name}
                        </span>{" "}
                        <span className="dashboard-graph-list-amount">
                          {formatMoney(repayment_amount, currency)}
                        </span>
                      </li>
                    );
                  })}
              </ul>
            ) : (
              <div></div>
            )}
          </Fragment>
        </div>
      </div>
    </div>
  );
};
export default RepaymentsGraph;
